/*
 * Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { createSlice } from "@reduxjs/toolkit";
import { get, set } from "lodash";
import { modelState } from "./model.state";
import { createModel, fetchModel, updateModel } from "./model.thunks";
import { Logger } from "cms/utils/log/logger";

const model = createSlice({
    
    name: "model",

    initialState: modelState,

    reducers: {
        setModelItem (state, action) {
            const modelItem = action?.payload;
            state.current = modelItem;
        },

        removeCurrentModelItem (state) {
            state.current = {};
        },

        /**
         * @param {import('@reduxjs/toolkit').PayloadAction<boolean>} action 
         */
        setLoading (state, action) {
            state.loading = action?.payload;
        },

        addComponent (state, action) {
            const fieldPosition = action?.payload?.fieldPosition;
            const component = action?.payload?.component;
            const componentIndex = action?.payload?.componentIndex;

            const componentsPath = [
                "fields", fieldPosition,
                "values", 0,
                "value"
            ];

            const components = get(state.current, componentsPath);
            let newComponents = [];

            if (Array.isArray(components)) {
                if (componentIndex == null) {
                    components.push(component);
                } else {
                    components.splice(componentIndex, 0, component);
                }
                newComponents = components.map((component, index) => ({
                    ...component,
                    position: index + 1
                }));
            } else {
                newComponents = [component];
            }
            set(state.current, componentsPath, newComponents);
        },
        setComponents (state, action) {
            const fieldPosition = action.payload?.fieldPosition;
            const components = action.payload?.components;

            const componentsPath = [
                "fields", fieldPosition,
                "values", 0,
                "value"
            ];

            set(state.current, componentsPath, components);
        },

        removeComponent (state, action) {
            const fieldPosition = action?.payload?.fieldPosition;
            const guid = action?.payload?.guid;

            const componentsPath = [
                "fields", fieldPosition,
                "values", 0,
                "value"
            ];

            const components = get(state.current, componentsPath, []);
            const filteredComponents = components.filter((component) => {
                return component.guid !== guid;
            });

            set (state.current, componentsPath, filteredComponents);
        },

        /**
         * Deeply set a path for a field value.
         * @param {import('@reduxjs/toolkit').PayloadAction<SetAtPathPayload>} action 
         */
        setFieldValueAtPath (state, action) {
            const path = action.payload?.path;
            const value = action.payload?.value;
            // use lodash to set this value to a path location in the state object
            // - path is an array like: ['fields', 0, 'settings', 'hide']
            // - value is any value like: false
            // - this would modify -> state.current.fields[0].settings.hide = false
            set(
                state.current,
                path,
                value
            );
        },

        /**
         * Sets multiple component properties within a field.
         * @param {import('@reduxjs/toolkit').PayloadAction<SetFieldComponentPayload>} action 
         */
        setFieldComponentProperties (state, action) {
            const guid = action?.payload?.guid;
            const fieldPosition = action?.payload?.fieldPosition;
            const properties = action?.payload?.properties;

            // TODO: move component resolution to a map
            // for components, we use the 0th index of the `values` array
            const componentsPath = [
                "fields", fieldPosition,
                "values", 0,
                "value"
            ];

            /** @type {ComponentLike[]} */
            const components = get(state.current, componentsPath, []);

            // find the index synchronously instead of relying on a static
            // index that may not be accurate
            const componentIndex = components.findIndex((component) => {
                return component.guid === guid;
            });

            if (componentIndex >= 0) {
                const targetComponent = components[componentIndex];

                for (const config of properties) {
                    const [path, value] = config;
                    set(targetComponent, path, value);
                }
            } else {
                const msg = `Could not find components at path "${componentsPath.join(".")}"; component ${guid} not saved`;
                Logger.warn(msg);
            }
        },
    },

    extraReducers (builder) {

        // update model
        builder.addCase(updateModel.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(updateModel.fulfilled, (state, action) => {
            state.current = action.payload;
            state.loading = false;
        });

        builder.addCase(updateModel.rejected, (state, action) => {
            // todo: properly record errors
            Logger.error("updateModel has an error: ", action.error);
            state.loading = false;
        });


        // create model
        builder.addCase(createModel.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(createModel.fulfilled, (state, action) => {
            state.current = action.payload;
            state.loading = false;
        });

        builder.addCase(createModel.rejected, (state, action) => {
            // todo: properly record errors
            Logger.error("createModel has an error: ", action.error);
            state.loading = false;
        });


        // fetch model
        builder.addCase(fetchModel.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(fetchModel.fulfilled, (state, action) => {
            const model = action.payload;
            state.current = model;
            state.loading = false;
        });

        builder.addCase(fetchModel.rejected, (state, action) => {
            // todo: properly record errors
            Logger.error("fetchModel has an error: ", action.error);
            state.loading = false;
        });
    }
});

export const modelReducer = model.reducer;

export const {
    addComponent,
    removeComponent,
    setComponents,
    removeCurrentModelItem,
    setFieldComponentProperties,
    setFieldComponentProperty,
    setFieldValueAtPath,
    setModelItem,
} = model.actions;


/**
 * @typedef {object} SetAtPathPayload
 * @property {string | string[]} path
 * @property {unknown} value
 */

/**
 * @typedef {object} SetFieldComponentPayload
 * @property {string} guid
 * @property {number} fieldPosition
 * @property {[path: string, value: any][]} properties
 */
